// Issue #22 Phase 7: a cart line with engraving now renders its own instance
// of this script (one per engraved line, cart page + drawer), on top of the
// single PDP instance. Without this guard, a second <script src> load of the
// same file re-declares the top-level `const`/`let` below in the same global
// scope and throws — same pattern as product-cart.js and most other assets
// in this theme that can legitimately load more than once per page.
if (!window.Eurus.loadedScript.has('engraving-modal.js')) {
  window.Eurus.loadedScript.add('engraving-modal.js');

/**
 * Engraving modal (issue #22, Phase 5).
 *
 * Per decision B1 this component never touches the network: commit() writes the
 * chosen text/font/tier into hidden inputs on the product form and closes. The
 * theme's normal Add to Cart submits them. See 00-architecture/decision-record.md §I.
 *
 * Tier price is resolved from the live tier table emitted by
 * snippets/engraving-config.liquid — never hardcoded (ADR §F).
 */
/**
 * Cap height and stroke weight of each engraving face, as a multiple of font
 * size, keyed by CSS family. Module-level rather than a component property so
 * caching a measurement never writes to Alpine's reactive data during a render.
 *
 * SVG's getBBox() on a <text> reports the em/line box vertically, not the ink
 * box — every glyph in a face returns the same height — so it cannot tell us
 * how tall the letters actually are. Canvas TextMetrics can: 'H' gives the real
 * cap height, and the ink thickness of '-' is a free proxy for the face's
 * stroke weight (2.6% of em on the hairline scripts here, 8.2% on Modern —
 * a 3x spread, which is why one hardcoded weight cannot suit all six).
 */
const ENGV_METRICS = {};
let ENGV_MEASURE_CTX = null;
const ENGV_PROBES = {};
const ENGV_FACE_READY = {};

/**
 * How long a first render will wait for a face before drawing in the fallback.
 * Warm cache resolves well inside this, so the usual path still draws in the
 * real face first time; a cold slow connection draws immediately and gets
 * corrected by confirmFace() when the face lands.
 */
const ENGV_FACE_WAIT_MS = 400;

/**
 * How long, and how often, to keep re-measuring a face after its first use.
 *
 * Re-measuring is the ONLY signal there is. Measured on the kit's CJK faces
 * over a 120 kbps link: `document.fonts` fired `loadingdone` at 8.1 s and
 * `document.fonts.ready` resolved with it, both while the family still
 * measured byte-identically to the fallback — the real metrics did not appear
 * until 24 s. A kit face is invisible to the Font Loading API (CLAUDE.md), so
 * neither event reports its arrival and neither can be waited on for it.
 *
 * The budget covers a large CJK face on a slow connection; the cost of being
 * wrong is a preview whose letter spacing stays fallback-shaped for the rest
 * of the session. Each poll is one canvas measureText.
 */
const ENGV_CONFIRM_BUDGET_MS = 30000;
const ENGV_CONFIRM_STEP_MS = 700;

/**
 * Wait until `family` is genuinely usable, not merely requested — once per
 * family per session, and never longer than ENGV_FACE_WAIT_MS.
 *
 * `document.fonts.load()` is not sufficient on its own for an Adobe Fonts kit:
 * the kit injects its @font-face rules at runtime and its faces never appear as
 * enumerable entries in `document.fonts`, so the promise can resolve while the
 * face is still unusable. Every canvas measurement below then reads the
 * fallback face — and `engvFontMetrics` caches that result per family for the
 * rest of the session, so one early measurement poisons every later render of
 * that font.
 *
 * Rendering a node in the family is what actually makes a browser fetch and
 * apply it, so the probe node below is the load trigger, not a formality. It
 * still cannot guarantee the face has *arrived* by the time this returns —
 * nothing in the Font Loading API reports that for a kit face — which is what
 * `confirmFace()` cleans up after.
 *
 * Two things here exist purely to keep typing responsive, and both matter on a
 * slow connection:
 *
 * - The result is cached per family. `document.fonts.ready` is DOCUMENT-wide:
 *   it stays pending while ANY face on the page is still loading, and the modal
 *   starts four Latin tiles fetching the moment it opens. Awaiting it on every
 *   keystroke meant the preview was gated on the whole page's font loading, so
 *   throttled to 400 kbps/400 ms RTT nothing was drawn at all for the first
 *   ~8 s of typing.
 * - The wait is capped. A first render must not sit behind the network either;
 *   drawing in the fallback and correcting on arrival is the honest trade, and
 *   confirmFace() already existed to make exactly that correction.
 */
/**
 * Add or remove the scrim from the browser's TOP LAYER.
 *
 * A z-index arms race is unwinnable: the scrim's 10000 was picked to clear the
 * Posloy loyalty launcher (9998) and its consent banner (9999), but any
 * third-party floating bubble can claim 2147483647 and no author value beats
 * it. The top layer paints above the ENTIRE normal layer regardless of
 * z-index, including a widget inside its own shadow root or an iframe, so
 * showPopover() settles it once instead of once per widget.
 *
 * Module-level, not a component method, deliberately: the first test in
 * engraving-modal.test.js pins the component's own field list, and a new
 * method would read there as a field needing an isolation assertion — which
 * this does not, since it holds no state and takes its element as an argument.
 *
 * Visibility stays Alpine's job. `x-show` writes an inline `display` that
 * outranks the UA's `[popover]:not(:popover-open){display:none}` in both
 * directions, so this only ever moves the layer, never whether the modal is on
 * screen — which is also why it cannot break the enter/leave transitions.
 */
function engvTopLayer(el, on) {
  if (!el || typeof el.showPopover !== 'function') return;
  try {
    // matches() throws on a pseudo-class the engine does not know, and
    // show/hidePopover throw on a redundant call — one guard covers both.
    if (on) {
      if (!el.matches(':popover-open')) el.showPopover();
    } else if (el.matches(':popover-open')) {
      el.hidePopover();
    }
  } catch (_) {
    /* Unsupported, or raced by another call. The z-index fallback stands. */
  }
}

function engvEnsureFace(family) {
  if (!family || !document.fonts) return Promise.resolve();
  let ready = ENGV_FACE_READY[family];
  if (ready) return ready;

  if (!ENGV_PROBES[family]) {
    // One probe per family, kept for the session. A single shared node cannot
    // hold four families at once, and detaching it can drop the fetch it was
    // added for.
    const probe = document.createElement('span');
    probe.setAttribute('aria-hidden', 'true');
    // Off-screen rather than display:none or visibility:hidden — both of those
    // let a browser skip the fetch, which is the one thing this node is for.
    probe.style.cssText = 'position:absolute;left:-9999px;top:0;font-size:120px;pointer-events:none';
    probe.style.fontFamily = '"' + family + '"';
    probe.textContent = 'H\u6c38';
    document.body.appendChild(probe);
    ENGV_PROBES[family] = probe;
  }

  const loaded = (async () => {
    try {
      await new Promise((r) => requestAnimationFrame(r));
      await document.fonts.load('100px "' + family + '"');
      await document.fonts.ready;
    } catch (e) {
      /* fall through and draw with whatever is available */
    }
  })();
  ready = ENGV_FACE_READY[family] = Promise.race([
    loaded,
    new Promise((r) => setTimeout(r, ENGV_FACE_WAIT_MS))
  ]);
  return ready;
}

/**
 * The one measuring canvas the module owns, with `font` already applied.
 * Every measurement below goes through here: a 2d context is the expensive
 * part, and the render path re-measures on every keystroke.
 */
function engvCtx(font) {
  ENGV_MEASURE_CTX = ENGV_MEASURE_CTX || document.createElement('canvas').getContext('2d');
  ENGV_MEASURE_CTX.font = font;
  return ENGV_MEASURE_CTX;
}

/** Fingerprint of how a family currently measures — changes when the real face lands. */
function engvFaceFingerprint(family) {
  const m = engvCtx('100px "' + family + '"').measureText('H\u6c38');
  return [m.width, m.actualBoundingBoxAscent, m.actualBoundingBoxDescent].join(',');
}

function engvFontMetrics(family, size) {
  let m = ENGV_METRICS[family];
  if (!m) {
    const ctx = engvCtx('100px "' + family + '"');
    const h = ctx.measureText('H');
    const cap = h.actualBoundingBoxAscent || 70;
    const bar = ctx.measureText('-');
    const barWeight = bar.actualBoundingBoxAscent - bar.actualBoundingBoxDescent;
    // A hyphen's bar reads a touch thinner than a stem, hence the 1.25; the
    // clamp keeps a face with a freak hyphen from producing a hairline or a
    // slab. Both bounds are relative to cap, so they hold at any font size.
    const weight = Math.min(Math.max((barWeight || cap * 0.08) * 1.25, cap * 0.05), cap * 0.14);
    // The face's own letter gap: what 'H' leaves unused inside its advance,
    // i.e. both side bearings. Negative on the scripts, which overhang their
    // advance, so clamp it into a band that still reads as a gap.
    const bearings = h.width - (h.actualBoundingBoxRight + h.actualBoundingBoxLeft);
    const gap = Math.min(Math.max(bearings, cap * 0.10), cap * 0.30);
    m = { cap: cap / 100, weight: weight / 100, gap: gap / 100 };
    ENGV_METRICS[family] = m;
  }
  return { cap: m.cap * size, weight: m.weight * size, gap: m.gap * size };
}

/**
 * Air each side of a symbol, as a multiple of the face's own natural letter
 * gap. A flat fraction of cap height cannot serve all six faces: Modern parks
 * 0.19em of side bearing around a capital while Romance overhangs its advance,
 * so the same constant reads airy in one and cramped in the other.
 */
const ENGV_SYMBOL_GAP = 1.2;

/**
 * Ink metrics of a text run — all four edges from ONE measurement, because
 * canvas reports both axes off the same TextMetrics and the render path used
 * to ask for the identical string twice per run, once per axis.
 *
 * Measured on canvas so overhang is included. The script faces overhang their
 * advance width by up to a third of an em (Romance 'H': 69.3 advance, ink out
 * to 93), so laying runs out by advance alone is what let a symbol land on top
 * of the letter before it.
 *
 * The two axes fall back independently, because they have to: the horizontal
 * one can borrow the DOM's getComputedTextLength(), and there is no vertical
 * equivalent of that API, so an unmeasurable face gets a plain fraction of
 * size instead. `top`/`bottom` are relative to a y=0 baseline, i.e. the ink
 * runs from -top to +bottom.
 */
function engvTextInk(node, value, family, size) {
  const m = engvCtx(size + 'px "' + family + '"').measureText(value);
  let left;
  let right;
  if (isFinite(m.actualBoundingBoxLeft) && isFinite(m.actualBoundingBoxRight)) {
    left = m.actualBoundingBoxLeft;
    right = m.actualBoundingBoxRight;
  } else {
    let w = 0;
    try { w = node.getComputedTextLength(); } catch (e) { w = 0; }
    left = 0;
    right = w || value.length * size * 0.5;
  }
  const vertical = isFinite(m.actualBoundingBoxAscent) && isFinite(m.actualBoundingBoxDescent);
  return {
    left: left,
    right: right,
    top: vertical ? m.actualBoundingBoxAscent : size * 0.72,
    bottom: vertical ? m.actualBoundingBoxDescent : size * 0.2
  };
}

// The preview draws these as SVG geometry, never as font glyphs. None of
// the six code points exists in all six supplied faces (U+2661 is in none
// of them), so rendering them as text would make the browser substitute a
// system font for the symbol alone — a heart in a different typeface from
// the letters beside it, misrepresenting what actually gets cut. Drawing
// them ourselves makes all six identical in every font, and identical
// between the picker tile and the preview (the tile draws the same path).
//
// Set matches Figma node 257:4 (Frame 52 -> "Add Symbol"): infinity, heart,
// star, flower, crescent moon, middle dot. The design shows each glyph in
// whatever face Figma had it in (Pinyon Script, Miniver, Moderustic, Times,
// Noto Serif SC) — the geometry below is those shapes, re-authored once so
// they no longer depend on a face at all.
//
// Geometry is authored in a 1000x1000 box; only its shape matters, because
// both the size and the position of every symbol are re-derived at render
// time from the SELECTED FONT's measured cap height:
//
//   height — ink height, as a multiple of cap height
//   center — where the ink centres vertically, in cap heights above baseline
//
// `stroke` is drawn as an outline (weight matched to the face), `fill` as
// solid; a symbol may carry both, which is what the flower's outlined
// petals plus solid centre needs.
//
// Nothing here is expressed in font-size units, and there is no per-symbol
// advance any more. Advance used to be a hand-guessed constant that had no
// relation to the geometry inside it, which is what made the spacing
// lopsided: the dot's ink overran its 0.5em advance on the right while
// leaving 0.35em on the left, and the infinity left 0.235em right against
// 0.085em left. The layout now measures the ink and puts the same air on
// both sides — see buildRunGroup.
//
// The line-item property still stores the real code point, so the tier
// counter, the cart display and the workshop are all unaffected.
const ENGV_SYMBOL_DEFS = [
    {
      char: '\u221e',
      labelKey: 'infinity',
      height: 0.5,
      center: 0.42,
      // Two tangent loops, not a crossing lemniscate: that is the shape the
      // design's face (Pinyon Script) draws, and it survives being scaled
      // down to half a cap height without the crossing turning to mush.
      stroke: 'M 40 500 a 230 230 0 1 0 460 0 a 230 230 0 1 0 -460 0 Z M 500 500 a 230 230 0 1 0 460 0 a 230 230 0 1 0 -460 0 Z'
    },
    {
      char: '\u2661',
      labelKey: 'heart',
      height: 0.8,
      center: 0.42,
      stroke: 'M 500 920 C 500 920, 90 660, 90 400 C 90 250, 200 150, 320 150 C 410 150, 470 200, 500 265 C 530 200, 590 150, 680 150 C 800 150, 910 250, 910 400 C 910 660, 500 920, 500 920 Z'
    },
    {
      char: '\u2729',
      labelKey: 'star',
      height: 0.78,
      center: 0.42,
      stroke: 'M 500 80 L 594.3 370.2 L 899.4 370.2 L 652.6 549.6 L 746.9 839.8 L 500 660.4 L 253.1 839.8 L 347.4 549.6 L 100.6 370.2 L 405.7 370.2 Z'
    },
    {
      char: '\u2740',
      labelKey: 'flower',
      height: 0.78,
      center: 0.42,
      stroke: 'M 500 500 C 372 392, 372 196, 500 120 C 628 196, 628 392, 500 500 Z M 500 500 C 563.2 344.9, 749.6 284.3, 861.4 382.6 C 828.7 527.8, 642.3 588.4, 500 500 Z M 500 500 C 667 512.1, 782.2 670.7, 723.4 807.4 C 575.1 821.2, 459.9 662.6, 500 500 Z M 500 500 C 540.1 662.6, 424.9 821.2, 276.6 807.4 C 217.8 670.7, 333 512.1, 500 500 Z M 500 500 C 357.7 588.4, 171.3 527.8, 138.6 382.6 C 250.4 284.3, 436.8 344.9, 500 500 Z',
      fill: 'M 500 500 m -105 0 a 105 105 0 1 0 210 0 a 105 105 0 1 0 -210 0 Z'
    },
    {
      char: '\u263d',
      labelKey: 'moon',
      height: 0.76,
      center: 0.42,
      stroke: 'M 727.8 171.2 A 400 400 0 1 0 727.8 828.8 A 330 330 0 0 1 727.8 171.2 Z'
    },
    {
      char: '\u00b7',
      labelKey: 'dot',
      height: 0.16,
      center: 0.42,
      fill: 'M 500 500 m -150 0 a 150 150 0 1 0 300 0 a 150 150 0 1 0 -300 0 Z'
    }
];

/**
 * Symbol lookup for splitting a typed string into runs. Module-level, like the
 * geometry above, because the renderer below has to work with no Alpine
 * component at all — the order-proof page re-renders a past order from its
 * saved recipe and has no modal, no strings and no picker.
 */
const ENGV_SYMBOL_MAP = {};
ENGV_SYMBOL_DEFS.forEach((sym) => { ENGV_SYMBOL_MAP[sym.char] = sym; });

/**
 * Split the string into runs so symbols can be drawn as geometry while the
 * letters around them stay in the chosen engraving font.
 * "LO♡VE" -> [text "LO"] [symbol ♡] [text "VE"]
 *
 * WHITESPACE IS ITS OWN RUN, and no text run ever contains any. That is not
 * tidiness, it is the fix for a real overlap: this layout positions every run
 * by its INK, and whitespace has none, so a space could only ever travel
 * inside a neighbouring run's string — where the two engines that have to
 * agree about it disagree.
 *
 *   canvas measureText(" U") -> actualBoundingBoxLeft = -18.46 at 70px serif
 *     (negative: the ink starts to the RIGHT of the origin, by one space)
 *   SVG <text> " U"          -> getNumberOfChars() === 1
 *
 * i.e. the browser COLLAPSES leading and trailing whitespace in SVG text and
 * `xml:space="preserve"` does not stop it (SVG 2 routes white-space through
 * CSS; Chrome reports the computed value as `nowrap` whatever xml:space says,
 * and only an actual `white-space: pre` declaration preserves the space).
 * So `x = pen + ink.left` moved the origin a space-width LEFT to make room for
 * a space the renderer then refused to draw, and the glyph landed a space-width
 * left of the pen — on top of the symbol before it, since the boundary gap is
 * far narrower than a space. "I❀ U" overlapped, "I❀U" did not, in every face.
 *
 * As its own run, whitespace becomes pure pen advance (see engvBuildRunGroup)
 * and no browser whitespace rule is in the path at all.
 */
function engvRuns(text) {
  const out = [];
  const last = () => out[out.length - 1];
  for (const ch of text) {
    const sym = ENGV_SYMBOL_MAP[ch];
    const type = sym ? 'symbol' : /\s/.test(ch) ? 'space' : 'text';
    if (type === 'symbol') {
      out.push({ type: 'symbol', sym });
    } else if (out.length && last().type === type) {
      last().value += ch;
    } else {
      out.push({ type: type, value: ch });
    }
  }
  return out;
}

/**
 * Advance width of a whitespace run in the chosen face — the one measurement
 * where advance, not ink, is the right answer, because a space is nothing but
 * advance. Falls back to a quarter em per character on a face canvas cannot
 * measure, which is the usual proportion for a Latin space.
 */
function engvSpaceAdvance(value, family, size) {
  const w = engvCtx(size + 'px "' + family + '"').measureText(value).width;
  return isFinite(w) && w > 0 ? w : value.length * size * 0.25;
}

/**
 * One symbol as a <g> of its parts, unscaled and untransformed — the caller
 * measures it and then places it. Stroke attributes live on the group so a
 * single stroke-width set later covers every stroked part, and so the lip
 * clone's [data-engv-paint] recolour still finds one node per paint kind.
 */
function engvSymbolGroup(sym, paint) {
  const NS = 'http://www.w3.org/2000/svg';
  const g = document.createElementNS(NS, 'g');
  if (sym.stroke) {
    g.setAttribute('stroke', paint);
    g.setAttribute('stroke-linecap', 'round');
    g.setAttribute('stroke-linejoin', 'round');
    g.setAttribute('data-engv-paint', 'stroke');
    const path = document.createElementNS(NS, 'path');
    path.setAttribute('d', sym.stroke);
    path.setAttribute('fill', 'none');
    g.appendChild(path);
  }
  if (sym.fill) {
    const path = document.createElementNS(NS, 'path');
    path.setAttribute('d', sym.fill);
    path.setAttribute('fill', paint);
    path.setAttribute('stroke', 'none');
    path.setAttribute('data-engv-paint', 'fill');
    g.appendChild(path);
  }
  return g;
}

/**
 * Lay the runs out into the (already document-attached) group g.
 *
 * Runs are positioned by their INK edges, not by advance width: `pen` is
 * where the next run's leading ink goes, and every boundary gets the same
 * gap. `pen` advances along x at a fixed y=0 baseline, one gap per run
 * boundary (text<->symbol — letters within one run need no extra gap, that's
 * what their own kerning is for; a whitespace run adds its own advance on top
 * of the one gap at its boundary, see the `space` branch).
 *
 * Returns the INK bounds of everything drawn, in group units, shaped like a
 * getBBox() result — or null if nothing measurable was drawn.
 *
 * That return value exists because getBBox() cannot supply it: vertically it
 * reports the em/line box (see the note on ENGV_METRICS), so its edges sit a
 * whole ascender above and a whole descender below the outermost stroke,
 * whether or not the string has an ascender or a descender at all. Every run
 * here is already POSITIONED by its ink, so the bounds are a by-product of
 * work the loop is doing anyway — `pen` is literally the flow-axis ink edge.
 */
function engvBuildRunGroup(g, text, size, family, paint) {
  const NS = 'http://www.w3.org/2000/svg';
  const metrics = engvFontMetrics(family, size);
  const gap = metrics.gap * ENGV_SYMBOL_GAP;
  let pen = 0;
  let first = true;
  let x0 = Infinity;
  let x1 = -Infinity;
  let y0 = Infinity;
  let y1 = -Infinity;
  const grow = (left, right, top, bottom) => {
    if (left < x0) x0 = left;
    if (right > x1) x1 = right;
    if (top < y0) y0 = top;
    if (bottom > y1) y1 = bottom;
  };

  engvRuns(text).forEach((run) => {
    if (run.type === 'space') {
      // Pure pen advance and nothing drawn: no node, no ink, no contribution
      // to the bounds. A LEADING space advances nothing at all (`first` is
      // still true), which is what keeps the block's own edge on its first
      // glyph — the same rule the trailing case gets for free, since the
      // bounds only ever grow around real ink.
      //
      // Returning before the gap line means a space run gets ONE boundary gap
      // in total (the one the run after it adds), not two, and the sum is
      // what makes a word space come out the width it should: this layout
      // positions by ink, so it has already stripped the side bearings either
      // side of the space that a natively-rendered space would have kept, and
      // `gap` is measured from exactly those bearings. Space advance alone came
      // out at 0.13 cap between two words where the face's own is 0.3.
      if (!first) pen += engvSpaceAdvance(run.value, family, size);
      return;
    }

    if (!first) pen += gap;
    first = false;

    if (run.type === 'text') {
      const node = document.createElementNS(NS, 'text');
      node.setAttribute('font-family', '"' + family + '"');
      node.setAttribute('font-size', size);
      node.setAttribute('fill', paint);
      node.setAttribute('data-engv-paint', 'fill');
      // No xml:space here on purpose: a text run never holds whitespace (see
      // engvRuns), and the attribute does not do what it looks like it does.
      node.textContent = run.value;
      g.appendChild(node);

      node.setAttribute('y', 0);
      const ink = engvTextInk(node, run.value, family, size);
      // x is the text origin; shift it so the run's leftmost ink — swash
      // overhang included — lands exactly on the pen.
      const x = pen + ink.left;
      node.setAttribute('x', x);
      // The cross axis comes off the same measurement: at a y=0 baseline the
      // ink runs from -ascent to +descent, which is what makes the returned
      // bounds sit on the letters rather than on the line box around them.
      grow(pen, x + ink.right, -ink.top, ink.bottom);
      pen = x + ink.right;
    } else {
      const sym = run.sym;
      const node = engvSymbolGroup(sym, paint);
      g.appendChild(node);

      // Measure the authored geometry rather than hardcoding its extents,
      // so editing a path above cannot silently desync the layout.
      // getBBox() excludes the stroke, which is what we want: the stroke
      // adds exactly `weight` to the ink box in each axis (round caps and
      // joins, so half a stroke past every extreme), and that lets scale
      // and stroke width be solved together instead of circularly:
      //   k * geometry + weight = target height
      let box;
      try { box = node.getBBox(); } catch (e) { box = null; }
      if (!box || !box.width || !box.height) return;

      const weight = sym.stroke ? metrics.weight : 0;
      // Target height is the symbol's own authored proportion of cap height.
      const targetH = sym.height * metrics.cap;
      const k = Math.max((targetH - weight) / box.height, 0.0001);
      // Stroke width is set in rendered units and divided back out by the
      // symbol's own scale, so a short wide symbol and a tall one come out
      // the same weight as each other and as the letters.
      if (weight) node.setAttribute('stroke-width', weight / k);

      // A symbol's own ink box is its scaled geometry grown by half a stroke at
      // every extreme (round caps and joins) — i.e. `weight` across each axis.
      // Ink left on the pen; ink centre at `center` cap heights up.
      const tx = pen + weight / 2 - box.x * k;
      const ty = -sym.center * metrics.cap - (box.y + box.height / 2) * k;
      node.setAttribute('transform', 'translate(' + tx + ' ' + ty + ') scale(' + k + ')');
      const mid = -sym.center * metrics.cap;
      const halfH = (box.height * k + weight) / 2;
      grow(pen, pen + box.width * k + weight, mid - halfH, mid + halfH);
      pen += box.width * k + weight;
    }
  });

  if (!isFinite(x0) || x1 <= x0 || y1 <= y0) return null;
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}

/**
 * Draw `text` into one preview SVG's fill and lip groups and return where it
 * actually landed.
 *
 * Split out of the Alpine component (issue #22, front/back faces) for two
 * reasons: the modal renders two faces from one component, and the order-proof
 * page renders a saved recipe with no component in the picture. It is
 * deliberately synchronous and does no font waiting — the caller owns that,
 * because only the caller knows whether it can afford to await (see
 * engvEnsureFace and the kit-face caveat in CLAUDE.md).
 *
 * Position and size come from the TEMPLATE alone — text_x / text_y as the
 * centre, text_width / text_height as the ceilings. There is no buyer-supplied
 * layout: the advanced placement controls (drag, resize, orientation,
 * alignment, frame) were withdrawn 2026-09-03, see CLAUDE.md § "Engraving
 * placement is fixed by the template".
 *
 * Returns `{natural: {w, h}, scale, x, y}` — the em bounding box of the run
 * group plus the transform actually applied, where `x`/`y` is that box's
 * CENTRE.
 */
function engvRender({ fillEl, lipEl, text, font, template, gradientId }) {
  fillEl.innerHTML = '';
  lipEl.innerHTML = '';
  if (!text) return null;

  const t = template || {};
  const size = (t.baseFontSize || 70) * (font ? font.scaleMultiplier || 1 : 1);
  const family = font ? font.cssFamily : 'serif';
  const paint = 'url(#' + gradientId + ')';

  // Group must be attached to the document before runs are built into it —
  // getComputedTextLength() on a still-detached node silently falls back to
  // an inaccurate 0.5em/char estimate, which is what let the next run's x
  // land short and overlap the previous glyph.
  const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  fillEl.appendChild(group);
  const ink = engvBuildRunGroup(group, text, size, family, paint);

  let emBox;
  try { emBox = group.getBBox(); } catch (e) { return null; }
  if (!emBox || !emBox.width) return null;

  // CENTRING uses getBBox()'s em/line box. text_x/text_y are the centre
  // deliberately so that the value is independent of the string and of each
  // face's ascender metrics (design-spec §11) — "ABC" and "aeo" have to centre
  // identically, which only an em box gives.
  const box = emBox;

  // The AXIS-ALIGNED footprint of the block once tilted, which is what actually
  // has to fit — not `box`, which measures the text before rotation. A tilt
  // costs far more vertical room than horizontal (a 200x60 block at 15 degrees
  // occupies 209x110: +4% wide, +83% tall), so fitting the untilted box is how
  // text ends up hanging off a charm that looked fine at 0.
  //
  // Both terms are needed on each axis because a rotated rectangle's extent is
  // the sum of its two sides' projections. At rotation 0 this collapses to
  // exactly box.width / box.height, so every existing template is unaffected.
  //
  // FITTING measures the INK while CENTRING above uses the em box. The two jobs
  // want different boxes: a ceiling is about what you can see, but a position
  // has to be string-independent. A script face reserves so much empty
  // ascender/descender that its em box can be twice the ink height, so fitting
  // the em box against text_height spends most of the merchant's budget on
  // blank space and renders the text far smaller than the number implies — and
  // by a different amount per font, which is precisely the drift text_height
  // exists to remove.
  const rad = ((t.textRotation || 0) * Math.PI) / 180;
  const absCos = Math.abs(Math.cos(rad));
  const absSin = Math.abs(Math.sin(rad));
  const fitBox = ink || emBox;
  const footW = fitBox.width * absCos + fitBox.height * absSin;
  const footH = fitBox.width * absSin + fitBox.height * absCos;

  // text_width is the hard ceiling the merchant sets; text_height is the
  // optional second one. Without text_height a tall face can still overflow
  // vertically — width alone has never constrained height, and a short string
  // never trips the width ceiling at all, which is why an oversized
  // base_font_size renders at full size no matter what text_width says.
  const maxW = t.textWidth || 450;
  const maxH = t.textHeight || 0;
  let scale = footW > maxW ? maxW / footW : 1;
  if (maxH && footH * scale > maxH) scale = maxH / footH;
  const targetX = t.textX || 500;
  const targetY = t.textY || 500;

  const cx = box.x + box.width / 2;
  const cy = box.y + box.height / 2;
  const tx = targetX - cx * scale;
  const ty = targetY + (font ? font.baselineOffset || 0 : 0) - cy * scale;

  // Tilt (text_rotation) is a whole-block rotation about the placed centre, so
  // it composes with the translate/scale above instead of replacing any of it —
  // unlike the withdrawn `arc`, which had to re-place every glyph (ADR §M).
  // Prepending it also rotates the lip's dy, which is what a carved shadow on
  // slanted text should do: the offset follows the baseline, not the viewport.
  const rot = t.textRotation ? 'rotate(' + t.textRotation + ' ' + targetX + ' ' + targetY + ') ' : '';
  group.setAttribute('transform', rot + 'translate(' + tx + ' ' + ty + ') scale(' + scale + ')');

  // The lip is the same geometry, offset down and drawn white behind.
  const lipNode = group.cloneNode(true);
  lipNode.setAttribute('transform', rot + 'translate(' + tx + ' ' + (ty + (t.lipDy || 0)) + ') scale(' + scale + ')');
  lipNode.querySelectorAll('[data-engv-paint]').forEach((el) => {
    const isStroke = el.getAttribute('data-engv-paint') === 'stroke';
    el.setAttribute(isStroke ? 'stroke' : 'fill', t.lipColor || '#ffffff');
  });
  lipEl.appendChild(lipNode);

  return { natural: { w: box.width, h: box.height }, scale: scale, x: targetX, y: targetY };
}

/**
 * The order-proof page (templates/page.engraving-proof.json) renders a past
 * order's engraving from the recipe stored on its line item, so it needs the
 * renderer without any of the modal around it.
 */
window.EngravingRender = engvRender;

/**
 * The pure step of the layout, hung off the renderer that already had to be
 * exported, so `assets/engraving-modal.test.js` can pin it under Node.
 *
 * The layout itself needs a real getBBox() and cannot leave the browser, but
 * the splitting DOES NOT — and it is where the "I❀ U overlaps, I❀U does not"
 * bug lived, so it is worth a test that cannot rot.
 */
window.EngravingRender.runs = engvRuns;
window.EngravingRender.symbols = ENGV_SYMBOL_DEFS;

document.addEventListener('alpine:init', () => {
  /**
   * Commit confirmation, held in a store rather than on the component.
   *
   * A cart-mode save ends by re-rendering the cart section, which destroys the
   * component that did the saving — so a toast owned by that component would
   * be torn down in the same frame it appeared. The store outlives the
   * re-render, and its markup lives in layout/theme.liquid, outside every
   * section reRenderSections touches.
   *
   * 5s matches xCartNoti.open()'s dwell so engraving feedback disappears on
   * the same rhythm as the theme's own add-to-cart notification.
   */
  Alpine.store('xEngravingToast', {
    message: '',
    timer: null,
    show(message) {
      // Cleared first so two saves in quick succession cannot have the first
      // one's timeout cut the second one's toast short.
      clearTimeout(this.timer);
      this.message = message;
      this.timer = setTimeout(() => { this.message = ''; }, 5000);
    }
  });

  Alpine.data('xEngravingModal', (config, modalId, closeId, strings, cartCtx, productFormId) => ({
    config,
    modalId,
    closeId,
    strings,
    cartCtx,
    productFormId,
    open: false,
    text: '',
    fontIndex: 0,
    committed: null,
    saving: false,
    saveError: '',

    /**
     * True for a few seconds after the `text` watcher removed an emoji, which
     * is the only thing that shows the buyer why what they typed disappeared.
     * `_emojiTimer` is the handle that clears it again.
     */
    emojiBlocked: false,
    _emojiTimer: null,

    /**
     * Which face the buyer is engraving (issue #22 front/back). Only ever
     * leaves 'front' when the product carries a second template metafield —
     * `config.templateBack` — so every existing product stays single-sided
     * with no merchant action. One face per engraving, never both: the client's
     * decision, and it is what keeps the fee, the tier and the cart line
     * exactly as they already are.
     */
    side: 'front',

    /**
     * The picker's view of ENGV_SYMBOL_DEFS: the geometry is shared with the
     * renderer (and with the order-proof page, which has no component and no
     * translations), so only the localised label is added here.
     */
    get symbols() {
      return ENGV_SYMBOL_DEFS.map(function (sym) {
        return Object.assign({}, sym, { label: this.strings[sym.labelKey] });
      }, this);
    },

    /**
     * Latin faces (~8-45 KB each) are cheap enough to paint tiles with
     * immediately. The two CJK faces are ~0.5 MB each, so their tiles stay in
     * the fallback face until picked — a webfont is only fetched once a node
     * actually renders in it, so this is what defers the ~1 MB (04-07).
     */
    activated: [],

    init() {
      this._confirmed = new Set();
      this.activated = this.config.fonts
        .filter((f) => f.charset !== 'cjk')
        .map((f) => f.cssFamily);

      // Cart-edit mode (issue #22 Phase 7): seed the draft from the line's
      // saved properties instead of starting blank, so reopening shows what
      // is actually on the order rather than nothing. Font is matched by
      // label since that's the only thing persisted on the line item; if the
      // merchant later drops that font from the allowed list, fall back to
      // index 0 rather than crashing on a missing selection.
      if (this.cartCtx) {
        const fontIndex = this.config.fonts.findIndex((f) => f.label === this.cartCtx.initialFontLabel);
        this.committed = {
          text: this.cartCtx.initialText,
          fontIndex: fontIndex >= 0 ? fontIndex : 0,
          fontLabel: this.cartCtx.initialFontLabel
        };
        this.text = this.committed.text;
        this.fontIndex = this.committed.fontIndex;
        if (this.selectedFont && !this.activated.includes(this.selectedFont.cssFamily)) {
          this.activated.push(this.selectedFont.cssFamily);
        }
        // Which face. Restored from the line's own property: an edit that
        // silently reset the back face to the front would rewrite what the
        // buyer already paid for.
        if (this.cartCtx.initialSide && this.hasBack) {
          this.side = /back/i.test(this.cartCtx.initialSide) ? 'back' : 'front';
          this.committed.side = this.side;
        }
        // The recipe still carries which FACE the line was engraved on, which
        // `Engraving Side` only records as a localised label. Its x/y/scale (and,
        // on a pre-2026-09-03 order, its orientation/align/frame segments) are
        // deliberately ignored: placement is the template's, so an edit re-reads
        // it live rather than replaying numbers a withdrawn control produced.
        const saved = EngravingLogic.parseProofUrl
          ? EngravingLogic.parseProofUrl(this.cartCtx.initialLayout)
          : null;
        if (saved && saved.side === 'back' && this.hasBack) this.side = 'back';
      }

      // PDP only: once Add to Cart has consumed this engraving it belongs to a
      // cart line, so drop the local draft and go back to offering a fresh
      // one. Without this the block kept showing "Edit"/"Update Engraving"
      // over a value that was no longer connected to anything — clicking it
      // rewrote hidden form inputs for an add that had already happened, so
      // the cart never changed and nothing appeared to work.
      //
      // The form id must MATCH, and an event that carries none is ignored
      // rather than treated as "applies to everyone". This is a document-level
      // listener on a page that can hold several of these components at once
      // (a PDP block, a quick-view of a different product, a featured-product
      // section), and every one of them hears every dispatch — so a detail-less
      // event used to wipe the unsent draft of every OTHER product's block as
      // well as the one that was actually added. The dispatcher always has the
      // form (product-cart.js reads the id off the form it just submitted, and
      // the engraving inputs only reach that submission through `form="<id>"`
      // in the first place), so requiring the match costs nothing real.
      if (!this.cartCtx) {
        this._onConsumed = (e) => {
          if (!e.detail || e.detail.formId !== this.productFormId) return;
          this.clearEngraving();
        };
        document.addEventListener('eurus:engraving:consumed', this._onConsumed);
      }

      let timer = null;
      this.$watch('text', (v) => {
        // `maxlength` on the input stops typing at the cap but not a paste
        // that ignores it, and an over-length draft renders a string the
        // template can never hold under a negative "characters remaining"
        // (QA row F5). canCommitEngraving already refused to commit it, so
        // this is about showing the buyer a legal draft, not about billing.
        // Assigned only when it actually changes, so the watcher cannot loop.
        //
        // Emoji are stripped BEFORE the cap is applied, so a pasted emoji never
        // eats one of the twelve slots on its way out. Unlike the cap, this one
        // says so: a maxlength stop is visible in the counter, but an emoji
        // vanishing silently just reads as a broken keyboard.
        //
        // The notice is only ever RAISED here, never lowered — `_emojiTimer` is
        // its sole owner. Assigning `this.emojiBlocked = clean !== v` on every
        // pass looks equivalent and is not: a strip always makes `capped !== v`
        // below, so the watcher reassigns `this.text` and Alpine re-runs it with
        // an already-clean value, whose `clean !== v` is false. Both runs flush
        // before a frame paints, so the banner was cleared before it could ever
        // be seen and the emoji appeared to vanish for no reason — which is the
        // exact failure it exists to prevent.
        const clean = EngravingLogic.stripEmoji(v);
        if (clean !== v) {
          this.emojiBlocked = true;
          clearTimeout(this._emojiTimer);
          this._emojiTimer = setTimeout(() => { this.emojiBlocked = false; }, 4000);
        }
        const capped = EngravingLogic.truncateToMax(clean, this.maxCharacters);
        if (capped !== v) {
          // Writing back through x-model reassigns el.value, and a browser puts
          // the caret at the END of a value it did not itself edit. Inserting an
          // emoji mid-string (every OS picker inserts at the caret) would then
          // throw the caret to the end of the line, which reads as the field
          // being broken. Re-derive it: whatever survives stripping in front of
          // the old caret is where the new one belongs.
          const el = this.engvEl('text');
          const at = el && el.selectionStart != null ? el.selectionStart : null;
          this.text = capped;
          if (at !== null) {
            const kept = Math.min(EngravingLogic.stripEmoji(v.slice(0, at)).length, capped.length);
            this.$nextTick(() => el.setSelectionRange(kept, kept));
          }
          return;
        }
        clearTimeout(timer);
        timer = setTimeout(() => this.renderPreview(), 120);
      });
      this.$watch('fontIndex', () => {
        const f = this.selectedFont;
        if (f && !this.activated.includes(f.cssFamily)) this.activated.push(f.cssFamily);
        this.renderPreview();
      });
      this.$watch('open', (v) => {
        if (v) this.renderPreview();
      });
      // Switching face changes the image, the geometry and the character cap
      // all at once, so it is a full re-render, not a re-paint.
      this.$watch('side', () => this.renderPreview());
    },

    /**
     * Alpine calls this when the component's element leaves the DOM — which on
     * the cart surfaces is every save, because reRenderSections replaces the
     * whole section's innerHTML and every engraved line's component with it.
     *
     * Both things released here outlive the component otherwise, and both are
     * document/window level, i.e. they are exactly the state that can reach a
     * DIFFERENT instance: the consumed listener would keep firing
     * clearEngraving() on a dead component, and the placement controller's
     * pointermove/pointerup/resize listeners would keep a detached overlay and
     * its closure alive for the rest of the session.
     */
    destroy() {
      if (this._onConsumed) {
        document.removeEventListener('eurus:engraving:consumed', this._onConsumed);
        this._onConsumed = null;
      }
      clearTimeout(this._emojiTimer);
    },

    /**
     * Alpine 3.10's $refs magic walks parentNode only — unlike its other DOM
     * lookups it never hops back over an x-teleport — so a ref registered on
     * the component root is invisible from inside the teleported dialog. Any
     * handler bound in there (the x-for symbol tiles) would silently get
     * undefined. Scope the query to the dialog id instead; that works from
     * either side of the teleport.
     */
    engvEl(name) {
      return document.getElementById(this.modalId)?.querySelector('[data-engv="' + name + '"]');
    },

    /** Faces already proven to measure the same twice — never re-checked. */
    _confirmed: null,

    /** The document-level consumed listener, so destroy() can take it off. */
    _onConsumed: null,

    /**
     * Re-measure a face shortly after its first use and re-render if the
     * numbers moved.
     *
     * An Adobe kit face can still be unusable when the first render measures
     * it, and the Font Loading API gives no signal for when that changes (see
     * engvEnsureFace). Measured on the kit's CJK faces: picking 简体 within a
     * second of page load put the symbol after the text ~3 viewBox units off,
     * and it stayed off, because `engvFontMetrics` had cached fallback metrics
     * under that family name. So the cache entry is dropped along with the
     * re-render. One check per face per session; a face that measures the same
     * twice is never looked at again.
     */
    confirmFace(font) {
      if (!font || !font.cssFamily) return;
      const family = font.cssFamily;
      // Claimed up front, not inside the poll: this is one watch per face per
      // session, and renderPreview calls this on every keystroke.
      if (this._confirmed.has(family)) return;
      this._confirmed.add(family);
      const before = engvFaceFingerprint(family);
      const deadline = performance.now() + ENGV_CONFIRM_BUDGET_MS;
      const poll = () => {
        if (engvFaceFingerprint(family) !== before) {
          delete ENGV_METRICS[family];
          this.renderPreview();
          return;
        }
        if (performance.now() < deadline) setTimeout(poll, ENGV_CONFIRM_STEP_MS);
      };
      setTimeout(poll, 900);
    },

    /** Tiles render in their own face only once that face is worth fetching. */
    tileFamily(font) {
      return this.activated.includes(font.cssFamily) ? font.cssFamily : 'inherit';
    },

    get selectedFont() {
      return this.config.fonts[this.fontIndex] || null;
    },

    /** Whether this product offers a choice of face at all. */
    get hasBack() {
      return !!this.config.templateBack;
    },

    /**
     * The active face's geometry, image, gradient, lip and character cap. Every
     * one of those is per-face because the back is a different photograph of a
     * possibly different surface. Fonts are the deliberate exception — they
     * come from the front template only, so switching faces cannot rewrite the
     * tile row under the buyer's selected index. See engraving-config.liquid.
     */
    get template() {
      if (this.side === 'back' && this.config.templateBack) return this.config.templateBack;
      return this.config.template;
    },

    /**
     * The active face's cap. `config.maxCharacters` is the front face's value
     * and is kept only as the fallback for a config emitted before this
     * feature existed.
     */
    get maxCharacters() {
      const t = this.template;
      return (t && t.maxCharacters) || this.config.maxCharacters;
    },

    /** Buyer-visible property value, and what the workshop reads on the order. */
    get sideLabel() {
      if (!this.hasBack) return '';
      return this.side === 'back'
        ? (this.strings.sideBack || 'Back')
        : (this.strings.sideFront || 'Front');
    },

    // Character counting, tier resolution and the commit gate are pure
    // logic with real revenue consequences (under/overcharging a buyer), so
    // they live in assets/engraving-logic.js where they're covered by
    // engraving-logic.test.js under Node's test runner — this component is
    // just DOM/Alpine glue over that module (loaded first, as EngravingLogic).
    get charCount() {
      return EngravingLogic.countCharacters(this.text);
    },

    get over() {
      return this.charCount > this.maxCharacters;
    },

    get remainingLabel() {
      const left = this.maxCharacters - this.charCount;
      return left + ' ' + this.strings.charactersRemaining;
    },

    get tierNote() {
      return EngravingLogic.formatTierNote(this.strings.tierNoteItem, this.config.tiers);
    },

    get activeTier() {
      return EngravingLogic.resolveTier(this.config.tiers, this.charCount);
    },

    get canCommit() {
      return EngravingLogic.canCommitEngraving(this.text, this.maxCharacters, this.config.tiers);
    },

    /**
     * The commit button says what the click will actually do. This has to be
     * computed here rather than baked into the markup by Liquid: the same
     * dialog serves create and edit, so a label chosen at render time is
     * already stale the moment an engraving is saved — which is exactly the
     * bug where reopening a committed engraving still read "Add Engraving".
     */
    get ctaLabel() {
      if (this.saving) return this.strings.saving;
      if (this.cartCtx) return this.strings.saveChangesCta;
      return this.committed ? this.strings.updateEngravingCta : this.strings.addEngravingCta;
    },

    /**
     * Create and edit are the same dialog over a draft. Opening always seeds the
     * draft from what is actually saved, so:
     *   - edit starts from the committed text/font, not from a half-typed
     *     session the buyer walked away from;
     *   - closing without saving discards the draft (decision B1 — the form is
     *     only ever written by commit()), and the next open shows the saved
     *     values again rather than the abandoned ones.
     * Create keeps its in-progress text across an accidental close, because
     * there is nothing saved to fall back to.
     */
    openModal() {
      if (this.committed) {
        this.text = this.committed.text;
        this.fontIndex = this.committed.fontIndex;
        // Reopening restores the saved face for the same reason it restores the
        // saved text: an edit starts from what is committed, never from an
        // abandoned half-session (decision B1).
        if (this.committed.side) this.side = this.committed.side;
      }
      // A failed save must not greet the buyer again on the next open.
      this.saveError = '';
      Alpine.store('xModal').setActiveElement(this.modalId);
      this.open = true;
      engvTopLayer(document.getElementById(this.modalId), true);
      Alpine.store('xPopup').open = true;
      Alpine.store('xModal').focus(this.modalId, this.closeId);
      // x-show keeps .engv-surface in the DOM across opens, so a prior scroll
      // position (or the text-input focus below) would otherwise carry into
      // the next open instead of showing the sheet's top.
      const surface = document.getElementById(this.modalId)?.querySelector('.engv-surface');
      if (surface) surface.scrollTop = 0;
      this.$nextTick(() => this.engvEl('text')?.focus({ preventScroll: true }));
    },

    closeModal() {
      Alpine.store('xModal').removeFocus();
      this.open = false;
      engvTopLayer(document.getElementById(this.modalId), false);
      Alpine.store('xPopup').close();
    },

    /** Insert at the caret rather than appending — the tiles are repeatable
     *  buttons, not a radio group (03-21). Caret is restored after the insert. */
    insertSymbol(char) {
      const el = this.engvEl('text');
      if (!el) return;
      const start = el.selectionStart ?? this.text.length;
      const end = el.selectionEnd ?? this.text.length;
      const next = this.text.slice(0, start) + char + this.text.slice(end);
      if (EngravingLogic.wouldExceedMax(next, this.maxCharacters)) return;
      this.text = next;
      this.$nextTick(() => {
        el.focus();
        el.setSelectionRange(start + char.length, start + char.length);
      });
    },

    commit() {
      if (!this.canCommit) return;
      if (this.cartCtx) {
        this.commitCartEdit();
        return;
      }
      const tier = this.activeTier;
      // Store the text as typed (not trimmed — leading/trailing spaces are
      // billed, see EngravingLogic.countCharacters) AND the font index: the
      // index is what reopening restores, while fontLabel is what the cart
      // line item carries.
      this.committed = {
        text: this.text,
        fontIndex: this.fontIndex,
        fontLabel: this.selectedFont ? this.selectedFont.label : '',
        variantId: tier.variantId,
        priceFormatted: tier.priceFormatted,
        // Fresh tag per commit — one engraving, one cart line, never merged
        // with an identical one. See EngravingLogic.newEngravingUid.
        uid: EngravingLogic.newEngravingUid(),
        // `side` is '' on a single-sided product and the hidden input drops an
        // empty value, so such a product's line carries no Front/Back property
        // at all. `proof` is now written for every engraved face (see
        // proofValue), which does change the line's property hash — harmless,
        // because `uid` above already mints a fresh tag per commit, so two
        // engraved lines never merge on hash in the first place.
        side: this.hasBack ? this.side : '',
        sideLabel: this.sideLabel,
        proof: this.proofValue
      };
      this.text = this.committed.text;
      this.closeModal();
      // Deliberately NO toast here. On the PDP nothing has left the page: this
      // only fills in hidden form inputs, and the cart is untouched until Add
      // to Cart. A success toast would read as "we saved something for you"
      // and imply the cart had changed. The summary panel replacing the
      // trigger is the honest, proportionate feedback for a local draft.
      // Toasts are reserved for cart-mode saves, which really do mutate the
      // cart — see commitCartEdit.
    },

    cartPost(url, body) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body)
      }).then((r) => r.json());
    },

    /**
     * Editing an engraving that's already a cart line (issue #22 Phase 7).
     * Same tier: one /cart/change.js patches the parent's properties in place.
     * Different tier: Shopify line items have no "swap variant" primitive, so
     * the fee child can't be patched — remove both this line and its fee
     * sibling, then re-add them together in one /cart/add.js call, exactly
     * mirroring the already-proven initial-add path in product-cart.js rather
     * than depending on undocumented behaviour for attaching a new child to
     * an already-existing parent line.
     *
     * Fail-closed per the ADR: if the re-add fails after the old lines are
     * already gone, immediately restore the original two lines rather than
     * leaving the buyer with no ring in their cart at all.
     */
    async commitCartEdit() {
      if (this.saving) return;
      if (!this.cartCtx.feeLineKey) {
        // No fee sibling found on the cart line — a data anomaly this UI
        // cannot safely resolve (which fee variant would it swap?). Fail
        // closed rather than guessing.
        this.saveError = this.strings.updateError;
        return;
      }
      const tier = this.activeTier;
      const text = this.text;
      const fontLabel = this.selectedFont ? this.selectedFont.label : '';
      const tierChanged = tier.variantId !== this.cartCtx.currentFeeVariantId;
      // Reuse the line's existing tag when it has one so an edit stays the same
      // line rather than becoming a second one; mint a fresh tag for a line
      // added before this feature existed.
      const uid = this.cartCtx.otherProperties?.[EngravingLogic.ENGRAVING_UID_KEY] || EngravingLogic.newEngravingUid();
      // Face and proof travel with the edit. `side` is dropped when blank, so a
      // single-sided line never gains a Front/Back property. `proof` is written
      // for every engraved face, and an EDIT reuses the line's existing `uid`
      // above, so the line is updated in place rather than forked.
      //
      // A line added BEFORE this change has no proof property; saving an edit
      // adds one. That is the intended upgrade path — the alternative is an
      // order the merchant cannot preview.
      const props = EngravingLogic.engravingProperties(text, fontLabel, this.cartCtx.otherProperties, uid, {
        [EngravingLogic.ENGRAVING_SIDE_KEY]: this.hasBack ? this.sideLabel : '',
        [EngravingLogic.ENGRAVING_PROOF_KEY]: this.proofValue
      });

      this.saving = true;
      this.saveError = '';
      const sections = Alpine.store('xCartHelper').getSectionsToRender().map((s) => s.id);
      const sectionsUrl = window.location.pathname;
      let removedOldLines = false;

      try {
        let response;
        if (!tierChanged) {
          response = await this.cartPost(`${Shopify.routes.root}cart/change.js`, {
            id: this.cartCtx.lineKey,
            quantity: this.cartCtx.quantity,
            properties: props,
            sections,
            sections_url: sectionsUrl
          });
          if (response.status == 422 || response.description) throw new Error(response.description || 'change failed');
        } else {
          await this.cartPost(`${Shopify.routes.root}cart/update.js`, {
            updates: {
              [this.cartCtx.lineKey]: 0,
              [this.cartCtx.feeLineKey]: 0
            }
          });
          removedOldLines = true;
          response = await this.cartPost(Eurus.cart_add_url, {
            items: [
              { id: this.cartCtx.variantId, quantity: this.cartCtx.quantity, properties: props },
              { id: tier.variantId, quantity: this.cartCtx.quantity, parent_id: this.cartCtx.variantId, properties: { For: this.cartCtx.productTitle } }
            ],
            sections,
            sections_url: sectionsUrl
          });
          if (response.status == 422 || response.description) throw new Error(response.description || 'add failed');
        }

        // Close BEFORE re-rendering. reRenderSections replaces the cart
        // section's innerHTML, which destroys this component and (via Alpine's
        // teleport cleanup) the dialog in <body> with it — so a closeModal()
        // afterwards would never run its store cleanup, leaving xPopup open
        // and the page scroll-locked with no visible modal to close.
        this.committed = {
          text,
          fontIndex: this.fontIndex,
          fontLabel,
          side: this.hasBack ? this.side : ''
        };
        this.saving = false;
        this.closeModal();
        // Toast before the re-render, since the re-render destroys this
        // component — the store keeps the message alive past that.
        Alpine.store('xEngravingToast').show(this.strings.toastUpdated);
        Alpine.store('xCartHelper').reRenderSections(response.sections);
        document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
      } catch (e) {
        if (removedOldLines) {
          const restoreProps = EngravingLogic.engravingProperties(
            this.cartCtx.initialText,
            this.cartCtx.initialFontLabel,
            this.cartCtx.otherProperties,
            null,
            {
              [EngravingLogic.ENGRAVING_SIDE_KEY]: this.cartCtx.initialSide || '',
              [EngravingLogic.ENGRAVING_PROOF_KEY]: this.cartCtx.initialLayout || ''
            }
          );
          await this.cartPost(Eurus.cart_add_url, {
            items: [
              { id: this.cartCtx.variantId, quantity: this.cartCtx.quantity, properties: restoreProps },
              { id: this.cartCtx.currentFeeVariantId, quantity: this.cartCtx.quantity, parent_id: this.cartCtx.variantId, properties: { For: this.cartCtx.productTitle } }
            ]
          }).then((r) => Alpine.store('xCartHelper').reRenderSections(r.sections)).catch(() => {});
        }
        this.saving = false;
        this.saveError = this.strings.updateError;
      }
    },

    clearEngraving() {
      this.committed = null;
      this.text = '';
      this.fontIndex = 0;
      this.side = 'front';
    },

    /**
     * Removing an engraving from an already-in-cart line (issue #22). The
     * ring stays in the cart — only the engraving comes off — so this is
     * two writes, not a line delete: strip the parent back down to
     * `otherProperties` (dropping the two engraving-only round-trip keys
     * commitCartEdit keeps), then zero out the fee sibling.
     *
     * The strip payload always carries the five engraving keys as explicit
     * empty strings rather than simply omitting them. `/cart/change.js`
     * REPLACES a line's properties with whatever non-empty object it's
     * given — proven by commitCartEdit's own edits — but an entirely EMPTY
     * `properties: {}` is silently ignored instead of clearing the line
     * (live-verified against the dev store): a ring with no properties
     * beyond the engraving ones would otherwise send `{}` and the old
     * Engraving/Engraving Font values would survive untouched, which is
     * exactly the bug this shipped with. Blank string values keep the
     * payload non-empty in every case, and `!= blank` is what every
     * consumer (engraving-line-context.liquid's `engv_text`, the cart
     * template's own property loop) already tests for "no value" — so a
     * key present-but-blank reads identically to one that was never there.
     *
     * Order matters for fail-closed safety, same principle as
     * commitCartEdit's swap-tier path: strip the parent FIRST. If that
     * fails, nothing has changed yet. If the fee removal that follows fails
     * instead, the dangerous state is a fee still charged against a line
     * that no longer shows what it paid for — so that failure restores the
     * parent's original engraving properties rather than leaving it there.
     * The reverse order would risk the opposite leak (engraving properties
     * left on the line with no fee attached) on the exact same failure.
     */
    async removeCartEngraving() {
      if (this.saving) return;
      if (!this.cartCtx.feeLineKey) {
        this.saveError = this.strings.updateError;
        return;
      }
      this.saving = true;
      this.saveError = '';
      const sections = Alpine.store('xCartHelper').getSectionsToRender().map((s) => s.id);
      const sectionsUrl = window.location.pathname;
      const strippedProps = Object.assign({}, this.cartCtx.otherProperties, {
        Engraving: '',
        'Engraving Font': '',
        [EngravingLogic.ENGRAVING_SIDE_KEY]: '',
        [EngravingLogic.ENGRAVING_UID_KEY]: '',
        [EngravingLogic.ENGRAVING_PROOF_KEY]: ''
      });
      let strippedParent = false;

      try {
        let response = await this.cartPost(`${Shopify.routes.root}cart/change.js`, {
          id: this.cartCtx.lineKey,
          quantity: this.cartCtx.quantity,
          properties: strippedProps
        });
        if (response.status == 422 || response.description) throw new Error(response.description || 'change failed');
        strippedParent = true;

        response = await this.cartPost(`${Shopify.routes.root}cart/update.js`, {
          updates: { [this.cartCtx.feeLineKey]: 0 },
          sections,
          sections_url: sectionsUrl
        });
        if (response.status == 422 || response.description) throw new Error(response.description || 'update failed');

        this.saving = false;
        Alpine.store('xEngravingToast').show(this.strings.toastRemoved);
        Alpine.store('xCartHelper').reRenderSections(response.sections);
        document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
      } catch (e) {
        if (strippedParent) {
          const restoreProps = EngravingLogic.engravingProperties(
            this.cartCtx.initialText,
            this.cartCtx.initialFontLabel,
            this.cartCtx.otherProperties,
            null,
            {
              [EngravingLogic.ENGRAVING_SIDE_KEY]: this.cartCtx.initialSide || '',
              [EngravingLogic.ENGRAVING_PROOF_KEY]: this.cartCtx.initialLayout || ''
            }
          );
          await this.cartPost(`${Shopify.routes.root}cart/change.js`, {
            id: this.cartCtx.lineKey,
            quantity: this.cartCtx.quantity,
            properties: restoreProps
          }).then((r) => Alpine.store('xCartHelper').reRenderSections(r.sections)).catch(() => {});
        }
        this.saving = false;
        this.saveError = this.strings.updateError;
      }
    },

    get gradientId() {
      return 'engv-grad-' + this.modalId;
    },

    get previewAriaLabel() {
      return this.text ? this.text : '';
    },

    /**
     * Measuring text before its webfont has loaded returns fallback-font
     * metrics, which makes the auto-fit scale wrong and visibly reflow. Gate
     * every render on the face actually being ready (05-04).
     *
     * The drawing itself now lives in the module-level engvRender so the same
     * code paints the modal, both faces, and the order-proof page. What stays
     * here is everything that needs the component: font readiness, the stale
     * render token, and the never-measure-a-hidden-preview guard.
     */
    async renderPreview() {
      const fill = this.engvEl('fill');
      const lip = this.engvEl('lip');
      if (!fill || !lip) return;

      const font = this.selectedFont;
      if (font) {
        await engvEnsureFace(font.cssFamily);
        // Covers every path into a face — first open, font switch, and a cart
        // line reopened on a font this session has not drawn yet.
        this.confirmFace(font);
      }
      if (this._renderToken !== undefined) this._renderToken++;
      else this._renderToken = 0;
      const token = this._renderToken;

      // Cleared ONLY on the empty-text path, never up front before the
      // requestAnimationFrame await below: engvRender() replaces the group
      // contents synchronously, so the previous render simply stays on screen
      // until the new one lands, instead of blanking for a frame on every
      // keystroke while a superseded render waits to fail its token check.
      if (!this.text) {
        fill.innerHTML = '';
        lip.innerHTML = '';
        return;
      }

      // EVERY measurement below runs through getBBox(), which returns zeros on a
      // display:none subtree — so wait a frame for Alpine's x-show to actually
      // put the dialog back on screen before measuring anything. This is the
      // edit path: reopening a saved engraving sets `open` with text already
      // present, and the watcher fired the render before x-show had restored
      // display. Every symbol then measured as 0 and was skipped, leaving its
      // path at raw 1000-unit author scale, which in turn blew up the group's
      // bbox and collapsed the fit-scale to ~0.47. Create never showed it only
      // because the text is empty on first open, so it returned above.
      await new Promise((r) => requestAnimationFrame(r));
      if (token !== this._renderToken) return;
      // Still not laid out (opened and closed inside one frame): leave the
      // preview empty rather than emitting unmeasured geometry. The open
      // watcher renders again on the next open.
      if (!fill.getClientRects().length && !fill.ownerSVGElement?.getClientRects().length) return;

      engvRender({
        fillEl: fill,
        lipEl: lip,
        text: this.text,
        font: font,
        template: this.template,
        gradientId: this.gradientId
      });
    },

    /**
     * The packed recipe that lets a past order be re-rendered exactly — text,
     * font, face and the template it was engraved on, in one string.
     *
     * Emitted for EVERY engraved face. It used to be free-placement only, on
     * the reasoning that a fixed template's text and font properties already
     * say everything the workshop needs. They do — but the person reading the
     * order still could not SEE the engraving, in the buyer's chosen face, on
     * the product photo, without opening the storefront and retyping it. That
     * is the whole job of the proof page. A link that is always there is also
     * one less thing for a merchant to have to know about: "some orders have a
     * proof and some don't" reads as a bug every single time.
     *
     * The recipe's x/y/scale are the TEMPLATE's own centre and 1.0 — placement
     * is fixed by the template, and the renderer ignores all three, re-reading
     * text_x/text_y/text_width/text_height live. They are seeded from the
     * template rather than left at the encoder's 500/500 default purely so the
     * numbers stay honest to anyone reading the raw property value.
     */
    get proofValue() {
      if (!this.template || !this.template.handle) return '';
      if (!EngravingLogic.encodeLayout || !EngravingLogic.proofUrl) return '';
      const recipe = EngravingLogic.encodeLayout({
        templateHandle: this.template.handle,
        side: this.side,
        x: this.template.textX || 500,
        y: this.template.textY || 500,
        scale: 1,
        fontFamily: this.selectedFont ? this.selectedFont.cssFamily : '',
        text: this.text.trim()
      });
      // Absolute, not root-relative: the admin order page renders property
      // values as plain text, so a relative path is not clickable for the
      // workshop. shopUrl carries no trailing slash and routes.root always
      // starts with one, which also keeps a localised store's /en-my/ prefix.
      const path = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const base = (this.strings.shopUrl || '') + path;
      return EngravingLogic.proofUrl(base, recipe);
    },

    /**
     * Picker tile artwork: the same geometry the preview draws, so the tile
     * cannot disagree with the result. Rendering the tile as the literal code
     * point instead would leave it at the mercy of whatever the device happens
     * to substitute — U+2729/U+2740/U+263D land in a different face per OS, and
     * some render them as colour emoji.
     */
    symbolTile(sym) {
      const w = sym.stroke ? 70 : 0;
      const pad = w / 2;
      const parts = [];
      if (sym.stroke) {
        parts.push('<path d="' + sym.stroke + '" fill="none" stroke="currentColor" stroke-width="' +
          w + '" stroke-linecap="round" stroke-linejoin="round"></path>');
      }
      if (sym.fill) parts.push('<path d="' + sym.fill + '" fill="currentColor"></path>');
      return '<svg viewBox="' + -pad + ' ' + -pad + ' ' + (1000 + w) + ' ' + (1000 + w) +
        '" aria-hidden="true" focusable="false">' + parts.join('') + '</svg>';
    },

  }));
});
}
